// ============================================================================
// 类型定义
// ============================================================================

/** 环境类型 */
export type Environment = "development" | "test" | "production" | "local";

/** 应用类型 */
export type AppType = "admin" | "tr" | "h5" | "c";

/** 布局类型（用于 SSO 登录页） */
export type LayoutType = "web" | "pad" | "mobile";

/** 域名来源 */
export type DomainSource = "domain" | "callback";

/** 品牌标识 */
export type BrandId = "default" | "bibl" | "zuu";

/** 租户编码 */
export type TenantCode = "default" | "bibl" | "zuu";

export type TenantResolutionMode = "fixed" | "username_suffix_required";

export interface AuthCopyConfig {
  actions: {
    verify: string;
    verifySuffixIcon: "none" | "arrow-right";
    submitDesktop: string;
    submitMobile: string;
    submitMobileSuffixIcon: "none" | "arrow-right";
  };
  messages: {
    missingCallback: string;
    missingProvider: string;
    callbackFailed: string;
    processingLogin: string;
    selectPasswordProviderFirst: string;
    verifyUsernameFirst: string;
    thirdPartyRedirectMissing: string;
    loginSuccess: string;
    passwordLoginDisabled: string;
  };
  validation: {
    usernameRequired: string;
    manualTenantUsernameFormat: string;
    passwordRequired: string;
  };
  placeholders: {
    username: string;
    usernameWithTenantCode: string;
    password: string;
  };
}

/** 品牌配置接口 */
export interface BrandConfig {
  /** 品牌 Logo 路径（将在运行时从 @ifa/ui 获取实际资源） */
  logo: string;
  /** 侧边栏纯色 Logo 路径（将在运行时从 @ifa/ui 获取实际资源） */
  solidLogo: string;
  /** 品牌名称 */
  name: string;
  /** 系统名称 */
  systemName: string;
  /** 系统简介 */
  systemDesc: string;
  /** Auth 页文案配置 */
  auth: AuthCopyConfig;
  /** Favicon 路径（将在运行时从 @ifa/ui 获取实际资源） */
  favicon: string;
  /** 版权信息 */
  copyright: string;
}

/** SSO Host 配置 */
interface TenantSSOHostConfig {
  /** Auth 域名（不包含协议） */
  hostname: string;
  /** 是否需要用户手动输入租户码（user@tenant-code 格式） */
  tenantResolutionMode: TenantResolutionMode;
}

/** 域名配置接口 */
interface DomainConfig {
  /** 域名匹配模式（支持字符串或正则表达式） */
  pattern: string | RegExp;
  /** API 基础地址 */
  apiBaseUrl: string;
  /** SSO 登录页地址 */
  authUrl: string;
  /** 应用类型（可选） */
  appType?: AppType;
  /** 是否为本地开发环境 */
  isLocal?: boolean;
}

/** 租户完整配置接口 */
export interface TenantFullConfig {
  /** 租户编码 */
  tenantCode: TenantCode;
  /** 租户显示名称 */
  displayName: string;
  /** 品牌标识 */
  brandId: BrandId;
  /** 品牌配置 */
  brand: BrandConfig;
  /** 域名配置列表 */
  domains: DomainConfig[];
  /** SSO 配置 */
  sso: {
    /** 默认 Auth 域名，用于租户码直解析或占位配置 */
    defaultHostname: string;
    /** 已激活的 Auth Host 列表 */
    hosts: TenantSSOHostConfig[];
  };
}

/** 租户配置接口（对外暴露） */
export interface TenantConfig {
  /** 域名（不包含协议，如：sso.cloud.bibl.com.hk） */
  hostname: string;
  /** 租户编码 */
  tenantCode: TenantCode;
  tenantResolutionMode: TenantResolutionMode;
  /** 是否需要用户手动输入租户码（user@tenant-code 格式） */
  requireManualTenantInput: boolean;
  /** 租户显示名称（可选） */
  displayName?: string;
}

/** 环境配置接口 */
export interface EnvConfig {
  apiBaseUrl: string;
  authUrl: string;
  appType?: AppType;
}

/** 全局配置接口 */
export interface GlobalConfig extends EnvConfig {
  currentEnv: Environment;
  isDevelopment: boolean;
  isTest: boolean;
  isProduction: boolean;
}

/** 租户解析结果 */
export interface TenantProfile {
  source: DomainSource;
  hostname: string;
  tenantCode: TenantCode;
  displayName?: string;
  brandId: BrandId;
  brandConfig: BrandConfig;
  tenantConfig: TenantConfig;
  envConfig: EnvConfig;
  layoutType: LayoutType;
  appType?: AppType;
  isGenericSSO: boolean;
  tenantResolutionMode: TenantResolutionMode;
  requireManualTenantInput: boolean;
  matchedDomainName?: string;
}

/** 向后兼容别名 */
export type DomainProfile = TenantProfile;

export interface ResolveDomainProfileOptions {
  runtimeTenantCode?: TenantCode | string | null;
}

// ============================================================================
// 统一租户配置
// ============================================================================

const runtimeEnv = import.meta.env ?? {};

/**
 * 统一租户配置表
 *
 * 将品牌配置、域名配置、SSO 配置统一管理
 * 添加新租户时，只需在此处添加一个新的配置对象
 */
const TENANT_FULL_CONFIGS: Record<TenantCode, TenantFullConfig> = {
  // ========== BIBL 租户配置 ==========
  bibl: {
    tenantCode: "bibl",
    displayName: "BIBL",
    brandId: "bibl",
    brand: {
      logo: "", // 将在运行时从 @ifa/ui 获取
      solidLogo: "", // 将在运行时从 @ifa/ui 获取
      name: "灣區雲服",
      systemName: "灣區雲服",
      systemDesc:
        "是一個專為現代保險顧問打造的智能訂單管理系統，助您高效處理每一份保障。",
      auth: {
        actions: {
          verify: "驗證",
          verifySuffixIcon: "none",
          submitDesktop: "確認登入",
          submitMobile: "立即登入",
          submitMobileSuffixIcon: "arrow-right",
        },
        messages: {
          missingCallback: "缺少回調地址，請重新發起登入流程",
          missingProvider: "缺少登入方式資訊，請重新發起登入流程",
          callbackFailed: "登入回調失敗，請重新嘗試",
          processingLogin: "正在處理登入，請稍候...",
          selectPasswordProviderFirst: "請先選擇密碼登入方式",
          verifyUsernameFirst: "請先驗證用戶名稱",
          thirdPartyRedirectMissing: "第三方登入地址缺失，請聯繫管理員",
          loginSuccess: "登入成功",
          passwordLoginDisabled:
            "當前系統未啟用密碼登入，請使用上方第三方登入方式。",
        },
        validation: {
          usernameRequired: "請輸入帳號",
          manualTenantUsernameFormat: "格式必須為user@tenant-code形式",
          passwordRequired: "請輸入密碼",
        },
        placeholders: {
          username: "請輸入用戶名稱",
          usernameWithTenantCode: "<用戶名稱>@<企業別名>",
          password: "請輸入密碼",
        },
      },
      favicon: "", // 将在运行时从 @ifa/ui 获取
      copyright: "© 2025 Bay Insurance Brokers Limited",
    },
    domains: [
      // 正式环境域名
      {
        pattern: "admin.cloud.bibl.com.hk",
        apiBaseUrl: "https://api.ifa.hk/api/v1",
        authUrl: "https://sso.cloud.bibl.com.hk/",
        appType: "admin",
      },
      {
        pattern: "cloud.bibl.com.hk",
        apiBaseUrl: "https://api.ifa.hk/api/v1",
        authUrl: "https://sso.cloud.bibl.com.hk/",
        appType: "tr",
      },
      {
        pattern: "h5.cloud.bibl.com.hk",
        apiBaseUrl: "https://api.ifa.hk/api/v1",
        authUrl: "https://sso.cloud.bibl.com.hk/",
        appType: "h5",
      },
      {
        pattern: "c.cloud.bibl.com.hk",
        apiBaseUrl: "https://api.ifa.hk/api/v1",
        authUrl: "https://sso.cloud.bibl.com.hk/",
        appType: "c",
      },
      {
        pattern: "sso.cloud.bibl.com.hk",
        apiBaseUrl: "https://api.ifa.hk/api/v1",
        authUrl: "https://sso.cloud.bibl.com.hk/",
      },
    ],
    sso: {
      defaultHostname: "sso.cloud.bibl.com.hk",
      hosts: [
        {
          hostname: "sso.cloud.bibl.com.hk",
          tenantResolutionMode: "fixed",
        },
      ],
    },
  },

  // ========== 默认租户配置（IFA） ==========
  default: {
    tenantCode: "default",
    displayName: "IFA",
    brandId: "default",
    brand: {
      logo: "", // 将在运行时从 @ifa/ui 获取
      solidLogo: "", // 将在运行时从 @ifa/ui 获取
      name: "IFA",
      systemName: "Broker System",
      systemDesc:
        "是一個專為現代保險顧問打造的智能訂單管理系統，助您高效處理每一份保障。",
      auth: {
        actions: {
          verify: "驗證",
          verifySuffixIcon: "none",
          submitDesktop: "確認登入",
          submitMobile: "立即登入",
          submitMobileSuffixIcon: "arrow-right",
        },
        messages: {
          missingCallback: "缺少回調地址，請重新發起登入流程",
          missingProvider: "缺少登入方式資訊，請重新發起登入流程",
          callbackFailed: "登入回調失敗，請重新嘗試",
          processingLogin: "正在處理登入，請稍候...",
          selectPasswordProviderFirst: "請先選擇密碼登入方式",
          verifyUsernameFirst: "請先驗證用戶名稱",
          thirdPartyRedirectMissing: "第三方登入地址缺失，請聯繫管理員",
          loginSuccess: "登入成功",
          passwordLoginDisabled:
            "當前系統未啟用密碼登入，請使用上方第三方登入方式。",
        },
        validation: {
          usernameRequired: "請輸入帳號",
          manualTenantUsernameFormat: "格式必須為user@tenant-code形式",
          passwordRequired: "請輸入密碼",
        },
        placeholders: {
          username: "請輸入用戶名稱",
          usernameWithTenantCode: "<用戶名稱>@<企業別名>",
          password: "請輸入密碼",
        },
      },
      favicon: "", // 将在运行时从 @ifa/ui 获取
      copyright: "© 2025 BEEFINTECH",
    },
    domains: [
      // 正式环境域名（ifa.hk）
      {
        pattern: "admin.ifa.hk",
        apiBaseUrl: "https://api.ifa.hk/api/v1",
        authUrl: "https://sso.ifa.hk/",
        appType: "admin",
      },
      {
        pattern: "tr.ifa.hk",
        apiBaseUrl: "https://api.ifa.hk/api/v1",
        authUrl: "https://sso.ifa.hk/",
        appType: "tr",
      },
      {
        pattern: "h5.ifa.hk",
        apiBaseUrl: "https://api.ifa.hk/api/v1",
        authUrl: "https://sso.ifa.hk/",
        appType: "h5",
      },
      {
        pattern: "c.ifa.hk",
        apiBaseUrl: "https://api.ifa.hk/api/v1",
        authUrl: "https://sso.ifa.hk/",
        appType: "c",
      },
      {
        pattern: "sso.ifa.hk",
        apiBaseUrl: "https://api.ifa.hk/api/v1",
        authUrl: "https://sso.ifa.hk/",
      },

      // 开发/测试环境域名（ifa.beeplatform.net）
      {
        pattern: "web.ifa.beeplatform.net",
        apiBaseUrl: "https://api.ifa.beeplatform.net/api/v1",
        authUrl: "https://sso.ifa.beeplatform.net/",
        appType: "admin",
      },
      {
        pattern: "tr.ifa.beeplatform.net",
        apiBaseUrl: "https://api.ifa.beeplatform.net/api/v1",
        authUrl: "https://sso.ifa.beeplatform.net/",
        appType: "tr",
      },
      {
        pattern: "wap.ifa.beeplatform.net",
        apiBaseUrl: "https://api.ifa.beeplatform.net/api/v1",
        authUrl: "https://sso.ifa.beeplatform.net/",
        appType: "h5",
      },
      {
        pattern: "c.ifa.beeplatform.net",
        apiBaseUrl: "https://api.ifa.beeplatform.net/api/v1",
        authUrl: "https://sso.ifa.beeplatform.net/",
        appType: "c",
      },
      {
        pattern: "sso.ifa.beeplatform.net",
        apiBaseUrl: "https://api.ifa.beeplatform.net/api/v1",
        authUrl: "https://sso.ifa.beeplatform.net/",
      },

      // 本地开发环境（支持 localhost、127.0.0.1 和局域网 IP）
      {
        pattern:
          /^(localhost|127\.0\.0\.1|192\.168\.\d{1,3}\.\d{1,3}|10\.\d{1,3}\.\d{1,3}\.\d{1,3}|172\.(1[6-9]|2[0-9]|3[0-1])\.\d{1,3}\.\d{1,3}):5173$/,
        apiBaseUrl: runtimeEnv.VITE_API_URL || "/api/v1",
        authUrl: runtimeEnv.VITE_AUTH_URL || "http://localhost:5175/",
        appType: "tr",
        isLocal: true,
      },
      {
        pattern:
          /^(localhost|127\.0\.0\.1|192\.168\.\d{1,3}\.\d{1,3}|10\.\d{1,3}\.\d{1,3}\.\d{1,3}|172\.(1[6-9]|2[0-9]|3[0-1])\.\d{1,3}\.\d{1,3}):5174$/,
        apiBaseUrl: runtimeEnv.VITE_API_URL || "/api/v1",
        authUrl: runtimeEnv.VITE_AUTH_URL || "http://localhost:5175/",
        appType: "admin",
        isLocal: true,
      },
      {
        pattern:
          /^(localhost|127\.0\.0\.1|192\.168\.\d{1,3}\.\d{1,3}|10\.\d{1,3}\.\d{1,3}\.\d{1,3}|172\.(1[6-9]|2[0-9]|3[0-1])\.\d{1,3}\.\d{1,3}):5175$/,
        apiBaseUrl: runtimeEnv.VITE_API_URL || "/api/v1",
        authUrl: runtimeEnv.VITE_AUTH_URL || "http://localhost:5175/",
        isLocal: true,
      },
      {
        pattern:
          /^(localhost|127\.0\.0\.1|192\.168\.\d{1,3}\.\d{1,3}|10\.\d{1,3}\.\d{1,3}\.\d{1,3}|172\.(1[6-9]|2[0-9]|3[0-1])\.\d{1,3}\.\d{1,3}):5177$/,
        apiBaseUrl: runtimeEnv.VITE_API_URL || "/api/v1",
        authUrl: runtimeEnv.VITE_AUTH_URL || "http://localhost:5175/",
        appType: "h5",
        isLocal: true,
      },
      {
        pattern:
          /^(localhost|127\.0\.0\.1|192\.168\.\d{1,3}\.\d{1,3}|10\.\d{1,3}\.\d{1,3}\.\d{1,3}|172\.(1[6-9]|2[0-9]|3[0-1])\.\d{1,3}\.\d{1,3})/,
        apiBaseUrl: runtimeEnv.VITE_API_URL || "/api/v1",
        authUrl: runtimeEnv.VITE_AUTH_URL || "http://localhost:5175/",
        isLocal: true,
      },
    ],
    sso: {
      defaultHostname: "sso.ifa.hk",
      hosts: [
        {
          hostname: "sso.ifa.hk",
          tenantResolutionMode: "username_suffix_required",
        },
        {
          hostname: "sso.ifa.beeplatform.net",
          tenantResolutionMode: "username_suffix_required",
        },
      ],
    },
  },

  // ========== ZUU 占位租户配置 ==========
  zuu: {
    tenantCode: "zuu",
    displayName: "ZUU",
    brandId: "zuu",
    brand: {
      logo: "",
      solidLogo: "",
      name: "ZSPACE",
      systemName: "ZSPACE",
      systemDesc: "專為現代獨立理財顧問 (IFA)\n打造的智能訂單管理平台，助您高效處理每一項保險業務。",
      auth: {
        actions: {
          verify: "下一步",
          verifySuffixIcon: "arrow-right",
          submitDesktop: "確認登入",
          submitMobile: "確認登入",
          submitMobileSuffixIcon: "none",
        },
        messages: {
          missingCallback: "缺少回調地址，請重新發起登入流程",
          missingProvider: "缺少登入方式資訊，請重新發起登入流程",
          callbackFailed: "登入回調失敗，請重新嘗試",
          processingLogin: "正在處理登入，請稍候...",
          selectPasswordProviderFirst: "請先選擇密碼登入方式",
          verifyUsernameFirst: "請先驗證用戶名稱",
          thirdPartyRedirectMissing: "第三方登入地址缺失，請聯繫管理員",
          loginSuccess: "登入成功",
          passwordLoginDisabled:
            "當前系統未啟用密碼登入，請使用上方第三方登入方式。",
        },
        validation: {
          usernameRequired: "請輸入帳號",
          manualTenantUsernameFormat: "格式必須為user@tenant-code形式",
          passwordRequired: "請輸入密碼",
        },
        placeholders: {
          username: "請輸入用戶名稱",
          usernameWithTenantCode: "user@tenant-code",
          password: "請輸入密碼",
        },
      },
      favicon: "",
      copyright: "© 2025 BEEFINTECH",
    },
    domains: [
      {
        pattern: "admin.zuu.ifa.hk",
        apiBaseUrl: "https://api.ifa.hk/api/v1",
        authUrl: "https://sso.zuu.ifa.hk/",
        appType: "admin",
      },
      {
        pattern: "tr.zuu.ifa.hk",
        apiBaseUrl: "https://api.ifa.hk/api/v1",
        authUrl: "https://sso.zuu.ifa.hk/",
        appType: "tr",
      },
      {
        pattern: "via.zuu.ifa.hk",
        apiBaseUrl: "https://api.ifa.hk/api/v1",
        authUrl: "https://sso.zuu.ifa.hk/",
        appType: "h5",
      },
      {
        pattern: "client.zuu.ifa.hk",
        apiBaseUrl: "https://api.ifa.hk/api/v1",
        authUrl: "https://sso.zuu.ifa.hk/",
        appType: "c",
      },
      {
        pattern: "sso.zuu.ifa.hk",
        apiBaseUrl: "https://api.ifa.hk/api/v1",
        authUrl: "https://sso.zuu.ifa.hk/",
      },
    ],
    sso: {
      defaultHostname: "sso.zuu.ifa.hk",
      hosts: [
        {
          hostname: "sso.zuu.ifa.hk",
          tenantResolutionMode: "fixed",
        },
      ],
    },
  },
};

// ============================================================================
// 从统一配置生成各个子配置
// ============================================================================

/** 域名配置列表（扁平化，用于域名匹配） */
const DOMAIN_CONFIGS: Array<
  DomainConfig & { tenantCode: TenantCode; name: string }
> = [];

/** 租户配置列表 */
export const TENANT_CONFIGS: TenantConfig[] = [];

/** SSO 通用域名列表 */
export const SSO_GENERIC_HOSTNAMES: string[] = [];

// 生成各个子配置
Object.entries(TENANT_FULL_CONFIGS).forEach(([, config]) => {
  // 生成租户配置
  config.sso.hosts.forEach((hostConfig) => {
    TENANT_CONFIGS.push({
      hostname: hostConfig.hostname,
      tenantCode: config.tenantCode,
      tenantResolutionMode: hostConfig.tenantResolutionMode,
      requireManualTenantInput:
        hostConfig.tenantResolutionMode === "username_suffix_required",
      displayName: config.displayName,
    });
  });

  // 生成域名配置（扁平化）
  config.domains.forEach((domain, index) => {
    DOMAIN_CONFIGS.push({
      ...domain,
      tenantCode: config.tenantCode,
      name: `${config.displayName} - ${domain.appType || "SSO"} (${index + 1})`,
    });
  });

  // 生成 SSO 通用域名列表
  config.sso.hosts.forEach((hostConfig) => {
    if (hostConfig.tenantResolutionMode === "username_suffix_required") {
      SSO_GENERIC_HOSTNAMES.push(hostConfig.hostname);
    }
  });
});

// 添加 localhost 到 SSO 通用域名（用于本地开发测试）
SSO_GENERIC_HOSTNAMES.push("localhost");

const DEFAULT_TENANT_CODE: TenantCode = "default";
const BEEPLATFORM_PREVIEW_HOSTNAME_SUFFIX = ".ifa.beeplatform.net";
const LOCAL_DEVELOPMENT_HOSTNAME_PATTERN =
  /^(localhost|127\.0\.0\.1|192\.168\.\d{1,3}\.\d{1,3}|10\.\d{1,3}\.\d{1,3}\.\d{1,3}|172\.(1[6-9]|2[0-9]|3[0-1])\.\d{1,3}\.\d{1,3})$/;

// ============================================================================
// 默认配置
// ============================================================================

/** 默认配置（当没有匹配到任何域名时使用） */
const DEFAULT_CONFIG: EnvConfig = {
  apiBaseUrl: runtimeEnv.VITE_API_URL || "https://api.ifa.hk/api/v1",
  authUrl: runtimeEnv.VITE_AUTH_URL || "https://sso.ifa.hk/",
};

/** 服务端渲染或构建时的默认配置 */
const SSR_DEFAULT_CONFIG: EnvConfig = {
  apiBaseUrl: runtimeEnv.VITE_API_URL || "/api/v1",
  authUrl: runtimeEnv.VITE_AUTH_URL || "https://sso.ifa.hk/",
};

// ============================================================================
// 工具函数
// ============================================================================

const isTenantCode = (value: string): value is TenantCode =>
  value in TENANT_FULL_CONFIGS;

export function mapRuntimeTenantSourceToTenantCode(
  value?: string | null,
): TenantCode | null {
  if (!value) return null;

  const normalized = value.trim().toLowerCase();

  if (normalized === "dev" || normalized === "default") {
    return "default";
  }

  return isTenantCode(normalized) ? normalized : null;
}

const getTenantFullConfig = (
  tenantCode?: TenantCode | string,
): TenantFullConfig => {
  if (tenantCode && isTenantCode(tenantCode)) {
    return TENANT_FULL_CONFIGS[tenantCode];
  }
  return TENANT_FULL_CONFIGS.default;
};

const getTenantFullConfigByBrandId = (brandId?: BrandId): TenantFullConfig => {
  if (!brandId) {
    return TENANT_FULL_CONFIGS.default;
  }

  const matchedConfig = Object.values(TENANT_FULL_CONFIGS).find(
    (config) => config.brandId === brandId,
  );

  return matchedConfig ?? TENANT_FULL_CONFIGS.default;
};

const getDefaultEnvConfig = (): EnvConfig => {
  if (typeof window === "undefined") {
    return SSR_DEFAULT_CONFIG;
  }
  return DEFAULT_CONFIG;
};

const normalizeHostname = (hostname: string): string =>
  hostname.trim().toLowerCase();

const isLocalDevelopmentRuntime = (hostname?: string): boolean => {
  const fallbackHostname =
    typeof window !== "undefined" ? window.location?.host || "" : "";
  const target =
    normalizeHostname(hostname || fallbackHostname).split(":")[0] ?? "";
  return LOCAL_DEVELOPMENT_HOSTNAME_PATTERN.test(target);
};

export function isRuntimeTenantPreviewHost(hostname?: string): boolean {
  const fallbackHostname =
    typeof window !== "undefined" ? window.location?.host || "" : "";
  const target =
    normalizeHostname(hostname || fallbackHostname).split(":")[0] ?? "";

  return (
    isLocalDevelopmentRuntime(target) ||
    target === "ifa.beeplatform.net" ||
    target.endsWith(BEEPLATFORM_PREVIEW_HOSTNAME_SUFFIX)
  );
}

const decodeValue = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const parseHostnameFromInput = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return "";

  const tryParse = (candidate: string): string | null => {
    try {
      return new URL(candidate).host;
    } catch {
      return null;
    }
  };

  const direct = tryParse(trimmed);
  if (direct) return direct;

  const decoded = decodeValue(trimmed);
  if (decoded !== trimmed) {
    const decodedParsed = tryParse(decoded);
    if (decodedParsed) return decodedParsed;
  }

  if (trimmed.includes("/") || trimmed.includes("?")) {
    const withScheme = tryParse(`http://${trimmed}`);
    if (withScheme) return withScheme;
  }

  if (decoded !== trimmed && (decoded.includes("/") || decoded.includes("?"))) {
    const withScheme = tryParse(`http://${decoded}`);
    if (withScheme) return withScheme;
  }

  return trimmed;
};

/**
 * AppType 到 LayoutType 的映射
 */
const APP_TYPE_TO_LAYOUT: Record<AppType, LayoutType> = {
  admin: "web",
  tr: "pad",
  h5: "mobile",
  c: "mobile",
};

const getLayoutTypeByAppType = (appType?: AppType): LayoutType => {
  if (appType) {
    return APP_TYPE_TO_LAYOUT[appType];
  }
  return "web";
};

const buildTenantConfig = (
  tenantCode?: TenantCode,
  hostname?: string,
): TenantConfig => {
  const fullConfig = getTenantFullConfig(tenantCode);
  const normalizedHostname = hostname
    ? normalizeHostname(hostname).split(":")[0]
    : "";
  const matchedHostConfig = normalizedHostname
    ? fullConfig.sso.hosts.find(
        (hostConfig) =>
          hostConfig.hostname.toLowerCase() === normalizedHostname,
      )
    : null;
  const defaultHostConfig =
    fullConfig.sso.hosts.find(
      (hostConfig) =>
        hostConfig.hostname.toLowerCase() ===
        fullConfig.sso.defaultHostname.toLowerCase(),
    ) ?? fullConfig.sso.hosts[0];

  return {
    hostname: matchedHostConfig?.hostname ?? fullConfig.sso.defaultHostname,
    tenantCode: fullConfig.tenantCode,
    tenantResolutionMode:
      matchedHostConfig?.tenantResolutionMode ??
      defaultHostConfig?.tenantResolutionMode ??
      "fixed",
    requireManualTenantInput:
      (
        matchedHostConfig?.tenantResolutionMode ??
        defaultHostConfig?.tenantResolutionMode ??
        "fixed"
      ) === "username_suffix_required",
    displayName: fullConfig.displayName,
  };
};

/**
 * 匹配域名配置
 *
 * 支持两种匹配模式：
 * 1. 字符串模式：精确匹配（如 'admin.ifa.hk' 只匹配 admin.ifa.hk）
 * 2. 正则表达式模式：使用正则表达式进行匹配（如 /^localhost/ 匹配所有以 localhost 开头的域名）
 *
 * @param hostname 主机名（包含端口号）
 * @returns 匹配的配置，如果没有匹配则返回 null
 */
const matchDomainConfig = (
  hostname: string,
): (DomainConfig & { tenantCode: TenantCode; name: string }) | null => {
  const lowerHostname = hostname.toLowerCase();

  for (const config of DOMAIN_CONFIGS) {
    if (typeof config.pattern === "string") {
      // 字符串精确匹配
      if (lowerHostname === config.pattern) {
        return config;
      }
    } else {
      // 正则表达式匹配
      if (config.pattern.test(lowerHostname)) {
        return config;
      }
    }
  }

  return null;
};

interface AuthLayoutResolution {
  hostname: string;
  appType?: AppType;
  layoutType: LayoutType;
  matchedDomainName?: string;
}

/**
 * 根据 callback target 推导认证页布局
 */
export function resolveAuthLayoutFromCallbackTarget(
  callbackTarget: string,
): AuthLayoutResolution {
  const rawHostname = parseHostnameFromInput(callbackTarget);
  const hostname = normalizeHostname(rawHostname);
  const matchedConfig = hostname ? matchDomainConfig(hostname) : null;
  const appType = matchedConfig?.appType;

  return {
    hostname,
    layoutType: getLayoutTypeByAppType(appType),
    ...(appType ? { appType } : {}),
    ...(matchedConfig?.name ? { matchedDomainName: matchedConfig.name } : {}),
  };
}

/**
 * 统一解析域名配置（支持当前域名或 callback URL）
 */
export function resolveDomainProfile(
  input: {
    source: DomainSource;
    value: string;
  },
  options?: ResolveDomainProfileOptions,
): TenantProfile {
  const rawHostname = parseHostnameFromInput(input.value);
  const hostname = normalizeHostname(rawHostname);
  const hostnameWithoutPort = hostname.split(":")[0];

  const matchedConfig = hostname ? matchDomainConfig(hostname) : null;
  const domainTenantCode = matchedConfig?.tenantCode ?? DEFAULT_TENANT_CODE;
  const runtimeTenantCode = isRuntimeTenantPreviewHost(hostnameWithoutPort)
    ? mapRuntimeTenantSourceToTenantCode(options?.runtimeTenantCode)
    : null;
  const resolvedTenantCode =
    runtimeTenantCode ?? domainTenantCode ?? DEFAULT_TENANT_CODE;
  const tenantFullConfig = getTenantFullConfig(resolvedTenantCode);
  const tenantConfig = buildTenantConfig(domainTenantCode, hostnameWithoutPort);
  const brandConfig = getBrandConfigByBrandId(tenantFullConfig.brandId);

  const envConfig: EnvConfig = matchedConfig
    ? {
        apiBaseUrl: matchedConfig.apiBaseUrl,
        authUrl: matchedConfig.authUrl,
        ...(matchedConfig.appType ? { appType: matchedConfig.appType } : {}),
      }
    : getDefaultEnvConfig();

  const callbackLayout =
    input.source === "callback"
      ? resolveAuthLayoutFromCallbackTarget(input.value)
      : null;
  const appType = callbackLayout?.appType ?? matchedConfig?.appType;
  const layoutType =
    callbackLayout?.layoutType ?? getLayoutTypeByAppType(appType);
  const matchedDomainName =
    callbackLayout?.matchedDomainName ?? matchedConfig?.name;
  const isGenericSSO = hostnameWithoutPort
    ? isGenericSSODomain(hostnameWithoutPort)
    : false;
  const tenantResolutionMode =
    tenantConfig.tenantResolutionMode ||
    (isGenericSSO ? "username_suffix_required" : "fixed");
  const requireManualTenantInput =
    tenantResolutionMode === "username_suffix_required";

  const profile: TenantProfile = {
    source: input.source,
    hostname,
    tenantCode: tenantFullConfig.tenantCode,
    brandId: tenantFullConfig.brandId,
    brandConfig,
    tenantConfig,
    envConfig,
    layoutType,
    isGenericSSO,
    tenantResolutionMode,
    requireManualTenantInput,
  };

  if (tenantFullConfig.displayName) {
    profile.displayName = tenantFullConfig.displayName;
  }
  if (appType) {
    profile.appType = appType;
  }
  if (matchedDomainName) {
    profile.matchedDomainName = matchedDomainName;
  }

  return profile;
}

/**
 * 根据环境变量自动确定当前环境
 */
const getEnv = (): Environment => {
  // 优先使用 Vite 提供的 MODE
  if (runtimeEnv.MODE) {
    if (runtimeEnv.DEV) return "development";

    const mode = runtimeEnv.MODE;
    if (mode === "production") return "production";
    if (mode === "test") return "test";
    if (mode === "development") return "development";
  }

  // 运行时环境检测（作为后备）
  if (typeof window !== "undefined") {
    const host = window.location.host;
    // 支持 localhost、127.0.0.1 和局域网 IP（192.168.x.x、10.x.x.x、172.16-31.x.x）
    if (
      host.includes("localhost") ||
      host.includes("127.0.0.1") ||
      /^(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/.test(host)
    ) {
      return "development";
    }
    if (host.includes("uat") || host.includes("test")) {
      return "test";
    }
  }

  return "production";
};

/**
 * 获取基于域名的配置
 */
const getDomainConfig = (): EnvConfig => {
  // 服务端渲染或构建时的默认配置
  if (typeof window === "undefined") {
    return SSR_DEFAULT_CONFIG;
  }

  // 获取主机名（包含端口号）
  const hostname = window.location.host;

  const profile = resolveDomainProfile({ source: "domain", value: hostname });

  if (profile.matchedDomainName) {
    console.log(
      `[Config] 匹配到配置: ${profile.matchedDomainName} (租户: ${profile.tenantCode})`,
    );
    return profile.envConfig;
  }

  // 没有匹配到，使用默认配置
  console.warn(`[Config] 未匹配到域名配置，使用默认配置: ${hostname}`);
  return profile.envConfig;
};

// ============================================================================
// 导出配置
// ============================================================================

const currentEnv = getEnv();

/** 全局配置对象 */
export const config: GlobalConfig = {
  ...getDomainConfig(),
  currentEnv,
  isDevelopment: currentEnv === "development",
  isTest: currentEnv === "test",
  isProduction: currentEnv === "production",
};

// 开发环境下打印配置信息
if (config.isDevelopment && typeof window !== "undefined") {
  console.log("[Config] 当前配置:", config);
}

// ============================================================================
// SSO 登录页布局类型映射
// ============================================================================

/**
 * 根据 callback URL 获取 SSO 登录页应该使用的布局类型
 *
 * 匹配逻辑：
 * 1. 从 callback URL 的 hostname 匹配 DOMAIN_CONFIGS
 * 2. 如果匹配到配置且有 appType，使用配置中的 appType 映射到 layoutType
 * 3. 默认返回 web 布局
 *
 * @param callbackUrl - SSO 回调 URL
 * @returns 布局类型（web | pad | mobile）
 *
 * @example
 * // 生产环境
 * getLayoutTypeFromCallback("https://tr.ifa.hk") // => "pad"
 * getLayoutTypeFromCallback("https://admin.ifa.hk") // => "web"
 *
 * // 本地开发（根据端口号）
 * getLayoutTypeFromCallback("http://localhost:5173") // => "pad"
 * getLayoutTypeFromCallback("http://localhost:5174") // => "web"
 * getLayoutTypeFromCallback("http://localhost:5176") // => "mobile"
 */
export function getLayoutTypeFromCallback(callbackUrl: string): LayoutType {
  const resolution = resolveAuthLayoutFromCallbackTarget(callbackUrl);

  if (resolution.appType) {
    console.log(
      `[Config] 从域名配置获取布局类型: ${resolution.matchedDomainName || resolution.hostname} -> ${resolution.appType} -> ${resolution.layoutType}`,
    );
    return resolution.layoutType;
  }

  console.log(
    `[Config] 未匹配到配置，使用默认布局: web (callback: ${callbackUrl})`,
  );
  return "web";
}

/**
 * 根据租户码获取品牌配置
 *
 * @param tenantCode - 租户码（如 "bibl"）
 * @returns 品牌配置对象
 *
 * @example
 * const brandConfig = getBrandConfig("bibl");
 * // => { logo: "...", name: "灣區雲服", systemName: "灣區雲服", favicon: "...", copyright: "..." }
 *
 * const defaultConfig = getBrandConfig();
 * // => { logo: "...", name: "灣區雲服", systemName: "灣區雲服", favicon: "...", copyright: "..." }
 */
export function getBrandConfig(tenantCode?: TenantCode | string): BrandConfig {
  return getTenantFullConfig(tenantCode).brand;
}

export function getBrandConfigByBrandId(brandId?: BrandId): BrandConfig {
  return getTenantFullConfigByBrandId(brandId).brand;
}

/**
 * 根据域名获取租户配置
 *
 * @param hostname - 域名（如 "sso.cloud.bibl.com.hk"）
 * @returns 租户配置对象，如果未找到则返回 null
 *
 * @example
 * const config = getTenantConfigByHostname("sso.cloud.bibl.com.hk");
 * // => { hostname: "sso.cloud.bibl.com.hk", tenantCode: "bibl", ... }
 */
export function getTenantConfigByHostname(
  hostname: string,
): TenantConfig | null {
  const target = hostname.toLowerCase();
  return (
    TENANT_CONFIGS.find((config) => config.hostname.toLowerCase() === target) ||
    null
  );
}

/**
 * 判断域名是否为 SSO 通用域名
 *
 * @param hostname - 域名
 * @returns 是否为通用 SSO 域名
 *
 * @example
 * isGenericSSODomain("sso.ifa.beeplatform.net") // => true
 * isGenericSSODomain("sso.cloud.bibl.com.hk") // => false
 */
export function isGenericSSODomain(hostname: string): boolean {
  const target = hostname.toLowerCase();
  return SSO_GENERIC_HOSTNAMES.some((item) => item.toLowerCase() === target);
}

/**
 * 获取当前环境的租户配置信息
 *
 * @returns 包含租户配置、是否为通用 SSO、是否需要手动输入租户码的对象
 *
 * @example
 * const { config, isGenericSSO, requireManualTenantInput } = getCurrentTenantConfig();
 * // config: TenantConfig | null
 * // isGenericSSO: boolean
 * // requireManualTenantInput: boolean
 */
export function getCurrentTenantConfig(): {
  config: TenantConfig | null;
  isGenericSSO: boolean;
  requireManualTenantInput: boolean;
} {
  const hostname = typeof window !== "undefined" ? window.location.host : "";
  const profile = resolveDomainProfile({ source: "domain", value: hostname });

  return {
    config: profile.tenantConfig,
    isGenericSSO: profile.isGenericSSO,
    requireManualTenantInput: profile.requireManualTenantInput,
  };
}
